/**
 * List of expected properties `Function` should have and these properties shouldn't be copied into
 * the synthetic exported module.
 */
const nativeProps = new Set([
  // Class properties
  'constructor',
  '__defineGetter__',
  '__defineSetter__',
  'hasOwnProperty',
  '__lookupGetter__',
  '__lookupSetter__',
  'isPrototypeOf',
  'propertyIsEnumerable',
  'toString',
  'valueOf',
  '__proto__',
  'toLocaleString',
  // Function properties
  'caller',
  'callee',
  'arguments',
]);

/**
 * Returns a synthetic export module with the `default` export extracted.
 *
 * This is to fix the type vs exported module mismatch in 2 scenarios:
 *
 * - For legacy modules that do both: export things and separately export a `default`.
 * - For importing CJS modules from ESM.
 *
 * There are many types of different behavior here: babel, esbuild, webpack, all treat the same
 * esModuleInterop differently, and even switch depending what version is used!
 *
 * This function is a workaround for that.
 *
 * Example 1:
 *
 * Importing `chalk@5` from ESM, has exports like `{ yellow, green }` etc. But importing the same
 * module from CJS, has exports like `{ default: { default: { yellow, green } } }`. The most
 * confusing part is that it's not a module that's the default export, but a function.
 *
 * Example 2:
 *
 * Blizzard is compiled with babel, which makes synthetic modules with `__esModule` property. So
 * when importing from ESM, it has exports like `{ __esModule, initBlizzard, default }`. But when
 * importing from CJS, it has `{ default }`.
 *
 * Example 3:
 *
 * Contentful uses namespace exports. When imported from ESM, it has `{ createClient,
 * createGlobalOptions }`. But when imported from CJS it has `{ contentful, default }`.
 *
 * See some history in: See https://github.com/evanw/esbuild/pull/1849
 *
 * @param maybeModule - The module to extract the default export from.
 * @param targetImport - The key to search for when recursively expanding nested default exports.
 */
export function importDefault<T extends object>(maybeModule?: T, targetImport?: string): T {
  if (typeof maybeModule !== 'object') return maybeModule as unknown as T;

  // Cloning the object because in ESM land modules aren't extensible.
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cant_define_property_object_not_extensible
  const result = { ...maybeModule } as Record<string, unknown>;
  let current: unknown = maybeModule;

  // Recursively expand nested default exports.
  // Note that the longest chain we have is 2 (for chalk), so adding 1 extra
  // iteration for unexpected cases.
  for (let i = 0; i < 3; i++) {
    if (current && typeof current !== 'object') break;

    // Quit if can't expand current further.
    if (!('default' in (current as object))) break;

    // Quit if already have the target import.
    if (targetImport && targetImport in result) break;

    const defaultExport = (current as { default: unknown }).default;

    if (typeof defaultExport === 'object') {
      Object.assign(result, defaultExport);
    } else if (typeof defaultExport === 'function') {
      for (const prop of Object.getOwnPropertyNames(Object.getPrototypeOf(defaultExport))) {
        if (nativeProps.has(prop)) continue;
        result[prop] = defaultExport[prop as keyof typeof defaultExport];
      }
    } else {
      // All other types do not need to be expanded.
      break;
    }

    current = defaultExport;
  }

  // Reseals the result.
  return Object.preventExtensions(result) as T;
}
